/**
 * Plugin Calendrier - Version modulaire avec gestion d'événements et agenda
 */

import React, { useState } from 'react';
import type { PluginInterface, PluginConfigProps } from '../types/PluginInterface';
import { generateUniformPlaceholder } from '../utils/PlaceholderGenerator';
import AutoTranslateText from '../components/AutoTranslateText';
import AdvancedCalendar, { CalendarConfig, CalendarEvent } from '../components/AdvancedCalendar';
import CalendarAnalytics from '../components/CalendarAnalytics';
import { fetchAPI } from '../services/api';

// Configuration par défaut étendue
const defaultCalendarConfig: CalendarConfig = {
  title: 'Mon Calendrier',
  description: 'Événements à venir',
  events: [],
  viewMode: 'month',
  theme: 'default',
  showWeekends: true,
  allowEventCreation: true,
  calendarHeight: 500,
  accentColor: '#0d6efd',
  backgroundColor: '#ffffff',
  textColor: '#333333',
  headerTextColor: '#0d6efd'
};

type CalendarOwnerConfig = CalendarConfig & {
  ownerId?: string | number;
  ownerEmail?: string;
};

const CalendarConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  const currentConfig = { ...defaultCalendarConfig, ...config } as CalendarOwnerConfig;
  const [activeTab, setActiveTab] = useState<'general' | 'style' | 'events'>('general');
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Associer le propriétaire au plugin pour les notifications
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    if (currentConfig.ownerId || currentConfig.ownerEmail) return;
    try {
      const userData = JSON.parse(storedUser);
      const ownerId = userData?.id || userData?.userId;
      const ownerEmail = userData?.email;
      if (ownerId || ownerEmail) {
        onConfigChange({
          ...currentConfig,
          ...(ownerId ? { ownerId: String(ownerId) } : {}),
          ...(ownerEmail ? { ownerEmail } : {})
        });
      }
    } catch {
      // ignore
    }
  }, [currentConfig, onConfigChange]);

  const addDefaultEvent = () => {
    const newEvent: CalendarEvent = {
        id: `evt_${Date.now()}`,
        title: 'Nouvel événement',
        date: new Date().toISOString().split('T')[0],
        category: 'meeting',
        priority: 'normal'
    };
    onConfigChange({ 
        ...currentConfig, 
        events: [...(currentConfig.events || []), newEvent] 
    });
  };

  const updateEvent = (idx: number, field: keyof CalendarEvent, value: any) => {
    const newEvts = [...(currentConfig.events || [])];
    const updatedEvent = { ...newEvts[idx], [field]: value } as CalendarEvent;
    if (field === 'endDate' && !value) {
      delete updatedEvent.endDate;
    }
    if (field === 'date' || field === 'endDate') {
      const start = updatedEvent.date;
      const end = updatedEvent.endDate;
      if (end && start && end < start) {
        updatedEvent.endDate = start;
      }
    }
    newEvts[idx] = updatedEvent;
    onConfigChange({...currentConfig, events: newEvts});
  };

  const deleteEvent = (idx: number) => {
      const newEvts = [...(currentConfig.events || [])];
      newEvts.splice(idx, 1);
      onConfigChange({...currentConfig, events: newEvts});
  };

  return (
    <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="m-0">Configuration Calendrier</h6>
            <button className="btn btn-sm btn-outline-info" onClick={() => setShowAnalytics(true)}>
                📊 Statistiques
            </button>
        </div>

        {showAnalytics && (
            <CalendarAnalytics config={currentConfig} onClose={() => setShowAnalytics(false)} />
        )}

        <ul className="nav nav-tabs mb-3">
            <li className="nav-item"><button className={`nav-link ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>⚙️ Général</button></li>
            <li className="nav-item"><button className={`nav-link ${activeTab === 'style' ? 'active' : ''}`} onClick={() => setActiveTab('style')}>🎨 Style</button></li>
            <li className="nav-item"><button className={`nav-link ${activeTab === 'events' ? 'active' : ''}`} onClick={() => setActiveTab('events')}>📅 Événements</button></li>
        </ul>

        {activeTab === 'general' && (
            <div className="animate-fade-in">
                <div className="mb-2">
                    <label className="form-label">Titre</label>
                    <input type="text" className="form-control" value={currentConfig.title} onChange={e => onConfigChange({...currentConfig, title: e.target.value})} />
                </div>
                <div className="mb-2">
                    <label className="form-label">Vue par défaut</label>
                    <select className="form-select" value={currentConfig.viewMode} onChange={e => onConfigChange({...currentConfig, viewMode: e.target.value as any})}>
                        <option value="month">Mois</option>
                        <option value="week">Semaine</option>
                        <option value="agenda">Agenda</option>
                    </select>
                </div>
                <div className="form-check mt-3">
                    <input 
                        type="checkbox" 
                        className="form-check-input" 
                        id="allowCreate"
                        checked={currentConfig.allowEventCreation} 
                        onChange={e => onConfigChange({...currentConfig, allowEventCreation: e.target.checked})} 
                    />
                    <label className="form-check-label" htmlFor="allowCreate">
                        Autoriser les visiteurs à créer des événements (Local)
                    </label>
                </div>
            </div>
        )}

        {activeTab === 'style' && (
            <div className="animate-fade-in">
                <div className="mb-3">
                     <label className="form-label">Couleur de fond</label>
                     <div className="input-group">
                        <span className="input-group-text p-1">
                            <input 
                                type="color" 
                                className="form-control form-control-color border-0 p-0" 
                                value={currentConfig.backgroundColor === 'transparent' ? '#ffffff' : currentConfig.backgroundColor} 
                                disabled={currentConfig.backgroundColor === 'transparent'}
                                onChange={e => onConfigChange({...currentConfig, backgroundColor: e.target.value})} 
                            />
                        </span>
                        <input 
                            type="text" 
                            className="form-control" 
                            value={currentConfig.backgroundColor} 
                            disabled={currentConfig.backgroundColor === 'transparent'}
                            onChange={e => onConfigChange({...currentConfig, backgroundColor: e.target.value})} 
                        />
                        <div className="input-group-text bg-white">
                            <div className="form-check mb-0">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    checked={currentConfig.backgroundColor === 'transparent'}
                                    onChange={(e) => onConfigChange({
                                        ...currentConfig, 
                                        backgroundColor: e.target.checked ? 'transparent' : '#ffffff'
                                    })}
                                />
                                <label className="form-check-label ms-1 small">Transparent</label>
                            </div>
                        </div>
                     </div>
                </div>
                
                <div className="row g-2 mb-3">
                    <div className="col-6">
                        <label className="form-label small">Couleur Texte</label>
                        <input type="color" className="form-control form-control-color w-100" value={currentConfig.textColor || '#333333'} onChange={e => onConfigChange({...currentConfig, textColor: e.target.value})} />
                    </div>
                    <div className="col-6">
                        <label className="form-label small">Couleur Accentuation</label>
                        <input type="color" className="form-control form-control-color w-100" value={currentConfig.accentColor || '#0d6efd'} onChange={e => onConfigChange({...currentConfig, accentColor: e.target.value})} />
                    </div>
                </div>
            </div>
        )}

        {/* ✅ ONGLET ÉVÉNEMENTS REDESIGNÉ */}
        {activeTab === 'events' && (
            <div className="animate-fade-in">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="small text-muted">{currentConfig.events?.length || 0} événements</span>
                    <button className="btn btn-sm btn-primary" onClick={addDefaultEvent}>
                        <i className="fas fa-plus me-1"></i> Ajouter
                    </button>
                </div>
                
                <div className="d-flex flex-column gap-2" style={{maxHeight: '400px', overflowY: 'auto'}}>
                    {(!currentConfig.events || currentConfig.events.length === 0) && (
                        <div className="text-center text-muted py-4 border rounded bg-light">
                            <div className="mb-2">📅</div>
                            Aucun événement configuré.
                        </div>
                    )}
                    {currentConfig.events?.map((evt, idx) => (
                        <div key={idx} className="card border shadow-sm">
                            <div className="card-body p-2">
                                {/* Ligne 1 : Titre et Supprimer */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <input 
                                        type="text" 
                                        className="form-control form-control-sm fw-bold border-0 px-1" 
                                        style={{ backgroundColor: 'transparent' }}
                                        value={evt.title}
                                        placeholder="Titre de l'événement"
                                        onChange={(e) => updateEvent(idx, 'title', e.target.value)}
                                    />
                                    <button 
                                        className="btn btn-link text-danger btn-sm p-0 ms-2" 
                                        onClick={() => deleteEvent(idx)}
                                        title="Supprimer"
                                    >
                                        🗑️
                                    </button>
                                </div>

                                {/* Ligne 2 : Dates */}
                                <div className="row g-1">
                                    <div className="col-6">
                                        <input 
                                            type="date" 
                                            className="form-control form-control-sm bg-light" 
                                            value={evt.date}
                                            onChange={(e) => updateEvent(idx, 'date', e.target.value)}
                                            title="Date de début"
                                        />
                                    </div>
                                    <div className="col-6">
                                        <input 
                                            type="date" 
                                            className="form-control form-control-sm bg-light" 
                                            value={evt.endDate || ''}
                                            min={evt.date}
                                            onChange={(e) => updateEvent(idx, 'endDate', e.target.value)}
                                            title="Date de fin (optionnel)"
                                        />
                                    </div>
                                </div>
                                {/* Ligne 3 : Catégorie, Priorité */}
                                <div className="row g-1 mt-1">
                                    <div className="col-3">
                                        <select 
                                            className="form-select form-select-sm" 
                                            value={evt.category}
                                            onChange={(e) => updateEvent(idx, 'category', e.target.value)}
                                            title="Catégorie"
                                        >
                                            <option value="work">💼 Pro</option>
                                            <option value="meeting">🤝 Réunion</option>
                                            <option value="personal">🏠 Perso</option>
                                            <option value="holiday">🏖️ Vacances</option>
                                        </select>
                                    </div>
                                    <div className="col-9">
                                        <div className="btn-group w-100" role="group">
                                            <button 
                                                className={`btn btn-sm py-0 px-1 ${evt.priority === 'low' ? 'btn-primary' : 'btn-outline-secondary'}`} 
                                                style={{fontSize: '10px'}}
                                                onClick={() => updateEvent(idx, 'priority', 'low')}
                                                title="Bas (Bleu)"
                                            >Bas</button>
                                            <button 
                                                className={`btn btn-sm py-0 px-1 ${!evt.priority || evt.priority === 'normal' ? 'btn-success' : 'btn-outline-secondary'}`} 
                                                style={{fontSize: '10px'}}
                                                onClick={() => updateEvent(idx, 'priority', 'normal')}
                                                title="Normal (Vert)"
                                            >Nor</button>
                                            <button 
                                                className={`btn btn-sm py-0 px-1 ${evt.priority === 'high' ? 'btn-danger' : 'btn-outline-secondary'}`} 
                                                style={{fontSize: '10px'}}
                                                onClick={() => updateEvent(idx, 'priority', 'high')}
                                                title="Haut (Rouge)"
                                            >Ha</button>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-1 d-flex justify-content-end">
                                    <div style={{
                                        height: '3px', 
                                        width: '100%', 
                                        backgroundColor: evt.priority === 'high' ? '#dc3545' : evt.priority === 'low' ? '#0d6efd' : '#198754',
                                        borderRadius: '2px',
                                        opacity: 0.5
                                    }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
  );
};

// --- DÉFINITION DU PLUGIN ---
const CalendarPluginModular: PluginInterface = {
  metadata: {
    id: 'calendar',
    name: 'Calendrier Avancé',
    description: 'Planning interactif.',
    version: '2.3.0',
    category: 'utility',
    icon: '📅',
    author: 'FlexiHub Team',
    tags: ['calendrier', 'agenda', 'planning', 'utilitaire', 'événements']
  },
  
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Calendrier agenda interactif avec événements multi-jours et édition en prévisualisation.",
    usage: "Afficher des plannings, créer et modifier des événements (dates simples ou plages), gérer les priorités.",
    configSchema: {
        title: "Titre du calendrier",
        viewMode: "'month' | 'week' | 'agenda'",
        allowEventCreation: "boolean",
        headerTextColor: "hex color",
        backgroundColor: "hex color or 'transparent'",
        events: "array of objects (id, title, date, endDate?, category, priority, description?, time?, endTime?, allDay?)"
    },
    exampleConfig: {
        title: "Planning Équipe",
        viewMode: "week",
        allowEventCreation: true,
        headerTextColor: "#ffffff",
        backgroundColor: "transparent",
        events: [
          {
            id: "evt_001",
            title: "Sprint Planning",
            date: "2026-02-09",
            endDate: "2026-02-11",
            category: "work",
            priority: "high"
          }
        ]
    }
  },

  defaultConfig: defaultCalendarConfig,
  ConfigComponent: CalendarConfigComponent,
  
  // ✅ MODIFICATION : On récupère onConfigChange pour persister les événements visiteurs
  PreviewComponent: ({ config, onConfigChange }) => (
    <AdvancedCalendar 
      config={config as CalendarConfig} 
      isPreview={true} 
      onEventAdd={(newEvent) => {
          // Si la fonction de mise à jour est disponible (mode éditeur), on sauvegarde
          if (onConfigChange) {
              const currentConfig = config as CalendarOwnerConfig;
              const currentEvents = currentConfig.events || [];
              onConfigChange({
                  ...currentConfig,
                  events: [...currentEvents, newEvent]
              });
          }

          // Notification au propriétaire (route publique)
          const ownerIdRaw = (config as CalendarOwnerConfig).ownerId;
          const ownerId = ownerIdRaw !== undefined && ownerIdRaw !== null ? Number(ownerIdRaw) : undefined;
          const ownerEmail = (config as CalendarOwnerConfig).ownerEmail;
          if (Number.isFinite(ownerId)) {
            fetchAPI('/calendar/notify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ownerId,
                ownerEmail,
                eventTitle: newEvent.title,
                eventId: newEvent.id,
                eventDate: newEvent.date
              })
            }).catch((err) => console.error('Erreur notification calendrier:', err));
          }
      }}
      onEventUpdate={(updatedEvent) => {
          if (!onConfigChange) return;
          const currentConfig = config as CalendarOwnerConfig;
          const currentEvents = currentConfig.events || [];
          const exists = currentEvents.some(event => event.id === updatedEvent.id);
          onConfigChange({
              ...currentConfig,
              events: exists
                ? currentEvents.map(event => event.id === updatedEvent.id ? updatedEvent : event)
                : [...currentEvents, updatedEvent]
          });
      }}
    />
  ),
  
  generateHTML: (config: CalendarConfig) => {
    const instanceId = config.id || `calendar-${Date.now()}`;
    return generateUniformPlaceholder({
      pluginId: instanceId,
      pluginType: 'calendar',
      title: config.title || 'Calendrier',
      description: `Vue ${config.viewMode}`,
      icon: '📅',
      category: 'utility', 
      config: { ...config, id: instanceId }
    });
  },
};

export default CalendarPluginModular;
